import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Descriptions } from 'antd';
import { LoadableButton, TableFooterWithActions } from '@components';
import { createShouldForwardProp } from '@functions';
import { DescriptionMode } from './helpers';

interface IStyledDescriptionsProps {
  hasActions?: boolean;
  mode?: DescriptionMode;
}

export const DescriptionsWrapper = styled(
  'div',
  createShouldForwardProp(['hasActions', 'mode']),
)<IStyledDescriptionsProps>`
  &&& {
    .ant-descriptions {
      &:first-of-type {
        background-color: var(--color-background-primary);
        margin-bottom: ${({ mode }) =>
          mode !== DescriptionMode.SINGLE && '8px'};
        border-radius: 6px;

        .ant-descriptions-row:nth-of-type(2n) {
          background: var(--color-background-secondary);
        }
      }

      ${({ mode }) =>
        mode === DescriptionMode.BIG &&
        css`
          &:nth-of-type(2) {
            border-top-left-radius: 6px;
            border-top-right-radius: 6px;
          }
        `}

      &:last-of-type {
        border-radius: ${({ mode }) => mode === DescriptionMode.SMALL && '6px'};
        border-bottom-left-radius: ${({ hasActions }) =>
          hasActions ? 0 : '6px'};
        border-bottom-right-radius: ${({ hasActions }) =>
          hasActions ? 0 : '6px'};
      }
    }
  }
`;

export const StyledDescriptions = styled(
  Descriptions,
  createShouldForwardProp([
    'hasActions',
    'skeletonRows',
    'isLoading',
    'extraButtons',
    'mobileViewWidth',
  ]),
)<IStyledDescriptionsProps>`
  &&& {
    position: relative;
    overflow: hidden;
    border-top: 1px solid var(--color-background-additional-main);
    border-right: 1px solid var(--color-background-additional-main);
    border-left: 1px solid var(--color-background-additional-main);
    border-bottom: 1px solid var(--color-background-additional-main);
    background-color: var(--color-background-secondary);

    .ant-descriptions-view > table {
      border-spacing: 0;
    }

    .ant-descriptions-item {
      padding: 4px 16px;
    }

    .ant-descriptions-header {
      padding: 4px 16px;
      background-color: var(--color-background-additional-main);
      margin: 0;
    }

    .ant-descriptions-title {
      font-size: 12px;
      font-weight: 500;
      line-height: 1.83;
      color: var(--color-field-title);
    }

    .ant-descriptions-row:nth-of-type(2n) {
      background: var(--color-background-primary);
    }

    .ant-descriptions-extra > button {
      display: flex;
      align-items: center;
      height: 22px;
      padding: 0 8px;
    }
  }
`;

export const LoadableButtonWrapper = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-column-gap: 8px;
`;

export const StyledLoadableButton = styled(LoadableButton)`
  &&& {
    font-size: 12px;

    span {
      line-height: 1;
    }
  }
`;

export const StyledFooterActions = styled(TableFooterWithActions)`
  border-top: none;
  border-right: 1px solid var(--color-background-additional-main);
  border-left: 1px solid var(--color-background-additional-main);
  border-bottom: 1px solid var(--color-background-additional-main);
`;

export const SkeletonWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  justify-content: space-between;
  grid-column-gap: 32px;
  padding: 4px 16px;
  border-right: 1px solid var(--color-background-additional-main);
  border-left: 1px solid var(--color-background-additional-main);
`;
